import type { TaskPriority, SuggestedGroup, AIProvider, AIModelTier } from '@/lib/types'

// ─── 무료 일일 사용 한도 ───
export const FREE_DAILY_LIMIT = 50

const VALID_PROVIDERS = new Set<AIProvider>(['anthropic', 'openai', 'google'])

/** 설정에서 현재 프로바이더와 API 키를 자동 해결 */
export function getAIConfig(): { provider: AIProvider; apiKey: string } {
  // 동적 import를 피하기 위해 localStorage에서 직접 읽음
  try {
    const raw = localStorage.getItem('todo-settings')
    if (raw) {
      const ai = JSON.parse(raw)?.state?.settings?.ai
      if (ai) {
        const provider: AIProvider = VALID_PROVIDERS.has(ai.provider) ? ai.provider : 'anthropic'
        const keyMap: Record<string, string> = { anthropic: ai.anthropicApiKey, openai: ai.openaiApiKey, google: ai.googleAIApiKey }
        return { provider, apiKey: keyMap[provider] || 'SERVER_KEY' }
      }
    }
  } catch { /* ignore */ }
  return { provider: 'anthropic', apiKey: 'SERVER_KEY' }
}

// ─── 프로바이더별 모델 매핑 ───
// 가격 모드: 저비용 모델 우선
const PROVIDER_MODELS: Record<AIProvider, Record<AIModelTier, string>> = {
  anthropic: {
    light: 'claude-haiku-4-5-20251001',
    standard: 'claude-sonnet-4-5-20250929',
  },
  openai: {
    light: 'gpt-4o-mini',
    standard: 'gpt-4o-mini',
  },
  google: {
    light: 'gemini-2.0-flash',
    standard: 'gemini-2.5-pro',
  },
}

// ─── 기능별 모델 티어 — 빠른 응답이 필요한 기능은 light ───
const FEATURE_TIER: Record<string, AIModelTier> = {
  parseTask: 'light',
  decomposeTask: 'light',
  productivity: 'light',
  grouping: 'light',
  nextSteps: 'light',
  chat: 'light',
  validate: 'light',
  recap: 'light',
}

interface AIMessage {
  role: 'user' | 'assistant'
  content: string
}

// ─── 프로바이더별 응답 텍스트 추출 ───
function extractText(provider: AIProvider, data: unknown): string {
  const d = data as Record<string, unknown>
  switch (provider) {
    case 'anthropic': {
      const content = d.content as { type: string; text?: string }[] | undefined
      return content?.find((b) => b.type === 'text')?.text || ''
    }
    case 'openai': {
      const choices = d.choices as { message: { content: string } }[] | undefined
      return choices?.[0]?.message?.content || ''
    }
    case 'google': {
      const candidates = d.candidates as { content: { parts: { text: string }[] } }[] | undefined
      return candidates?.[0]?.content?.parts?.[0]?.text || ''
    }
  }
}

/** 통합 AI 호출 — 멀티 프로바이더 지원 */
export async function callAI(
  provider: AIProvider,
  apiKey: string,
  systemPrompt: string,
  messages: AIMessage[],
  feature = 'chat',
  maxTokens = 1024
): Promise<string> {
  if (!apiKey?.trim()) {
    throw new Error('API 키가 설정되지 않았습니다. 설정에서 API 키를 입력해주세요.')
  }

  const tier = FEATURE_TIER[feature] || 'standard'
  const model = PROVIDER_MODELS[provider][tier]

  const { httpsCallable } = await import('firebase/functions')
  const { getFunctionsInstance } = await import('@/lib/firebase')
  const functions = await getFunctionsInstance()
  const proxy = httpsCallable(functions, 'aiProxy', { timeout: 120_000 })

  try {
    const result = await proxy({
      provider,
      apiKey: apiKey.trim(),
      model,
      system: systemPrompt,
      messages,
      max_tokens: maxTokens,
      feature,
    })

    return extractText(provider, result.data)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : ''
    if (msg.includes('유효하지 않습니다') || msg.includes('permission-denied')) {
      throw new Error('API 키가 유효하지 않습니다. 설정에서 확인해주세요.', { cause: err })
    }
    if (msg.includes('너무 많습니다') || msg.includes('resource-exhausted')) {
      throw new Error('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.', { cause: err })
    }
    if ((err as { code?: string })?.code === 'functions/unauthenticated') {
      throw new Error('Firebase 로그인이 필요합니다.', { cause: err })
    }
    throw new Error(msg || 'AI 요청에 실패했습니다. 다시 시도해주세요.', { cause: err })
  }
}

// ─── AI Agent 호출 (LangGraph) ──────────────────────

export interface AgentAction {
  type: string
  title?: string
  id?: string
}

export interface AgentToolCall {
  name: string
  args: Record<string, unknown>
  result: string
}

export interface AgentResponse {
  response: string
  actions: AgentAction[]
  toolCalls: AgentToolCall[]
  interrupted?: boolean
  interruptValue?: unknown
  threadId?: string
}

/** LangGraph 에이전트 호출 — 도구 사용 + HITL + 대화 메모리 */
export async function callAgent(
  provider: AIProvider,
  apiKey: string,
  message: string,
  threadId?: string,
  context?: string
): Promise<AgentResponse> {
  if (!apiKey?.trim()) {
    throw new Error('API 키가 설정되지 않았습니다. 설정에서 API 키를 입력해주세요.')
  }

  const { httpsCallable } = await import('firebase/functions')
  const { getFunctionsInstance } = await import('@/lib/firebase')
  const functions = await getFunctionsInstance()
  const agent = httpsCallable(functions, 'aiAgent', { timeout: 120_000 })

  try {
    const result = await agent({
      provider,
      apiKey: apiKey.trim(),
      message,
      threadId,
      context,
    })

    const data = result.data as AgentResponse
    return {
      response: data.response || '',
      actions: data.actions || [],
      toolCalls: data.toolCalls || [],
      interrupted: data.interrupted || false,
      interruptValue: data.interruptValue,
      threadId: data.threadId,
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : ''
    if (msg.includes('permission-denied')) throw new Error('API 키가 유효하지 않습니다.', { cause: err })
    if (msg.includes('resource-exhausted')) throw new Error('요청이 너무 많습니다.', { cause: err })
    if ((err as { code?: string })?.code === 'functions/unauthenticated') throw new Error('Firebase 로그인이 필요합니다.', { cause: err })
    throw new Error(msg || 'AI 에이전트 실행에 실패했습니다.', { cause: err })
  }
}

/** HITL resume — 에이전트 중단 후 사용자 승인/거부 */
export async function resumeAgent(
  provider: AIProvider,
  apiKey: string,
  threadId: string,
  approved: boolean
): Promise<AgentResponse> {
  const { httpsCallable } = await import('firebase/functions')
  const { getFunctionsInstance } = await import('@/lib/firebase')
  const functions = await getFunctionsInstance()
  const agent = httpsCallable(functions, 'aiAgent', { timeout: 120_000 })

  const result = await agent({
    provider,
    apiKey: apiKey.trim(),
    threadId,
    resumeValue: { approved },
  })

  const data = result.data as AgentResponse
  return {
    response: data.response || '',
    actions: data.actions || [],
    toolCalls: data.toolCalls || [],
    interrupted: data.interrupted || false,
    interruptValue: data.interruptValue,
    threadId: data.threadId,
  }
}

/** 하위 호환 래퍼: 기존 callClaude 호출자 지원 */
export async function callClaude(
  apiKey: string,
  systemPrompt: string,
  messages: AIMessage[],
  maxTokens = 1024
): Promise<string> {
  return callAI('anthropic', apiKey, systemPrompt, messages, 'chat', maxTokens)
}

// ─── API Key Validation ─────────────────────────────

export async function validateApiKey(apiKey: string, provider: AIProvider = 'anthropic'): Promise<boolean> {
  try {
    const result = await callAI(
      provider,
      apiKey,
      'Respond with exactly: OK',
      [{ role: 'user', content: 'ping' }],
      'validate',
      16
    )
    return result.includes('OK') || result.length > 0
  } catch {
    return false
  }
}

// ─── Natural Language Task Parsing ──────────────────

export interface ParsedTask {
  title: string
  dueDate?: string
  priority: TaskPriority
  categoryHint?: string
  memo?: string
}

export async function parseTaskWithAI(
  provider: AIProvider,
  apiKey: string,
  input: string,
  categories: string[]
): Promise<ParsedTask> {
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const dayOfWeek = now.toLocaleDateString('ko-KR', { weekday: 'long' })

  const systemPrompt = `You are a task parsing assistant for a Korean to-do list app.
Parse the user's natural language input into structured task data.

Today: ${today} (${dayOfWeek})
Available categories: ${categories.join(', ')}

Return ONLY valid JSON (no markdown, no explanation):
{
  "title": "extracted task title",
  "dueDate": "YYYY-MM-DD or null",
  "priority": "none" | "low" | "medium" | "high",
  "categoryHint": "matching category name or null",
  "memo": "any additional notes or null"
}

Rules:
- Extract dates from expressions like "내일", "금요일", "다음주 월요일", "3일 후"
- Map priority keywords: "급한/긴급/중요" → high, "보통" → medium, "낮은/나중에" → low
- Match category hints to the available categories
- Keep the title clean and actionable
- Respond in Korean context`

  const result = await callAI(
    provider,
    apiKey,
    systemPrompt,
    [{ role: 'user', content: input }],
    'parseTask',
    512
  )

  try {
    const jsonMatch = result.match(/\{[\s\S]*\}/)
    if (!jsonMatch) throw new Error('JSON not found')
    const parsed = JSON.parse(jsonMatch[0])
    return {
      title: parsed.title || input,
      dueDate: parsed.dueDate || undefined,
      priority: (['none', 'low', 'medium', 'high'].includes(parsed.priority) ? parsed.priority : 'none') as TaskPriority,
      categoryHint: parsed.categoryHint || undefined,
      memo: parsed.memo || undefined,
    }
  } catch {
    return { title: input, priority: 'none' }
  }
}

// ─── Task Decomposition ────────────────────────────

export interface DecomposedSubtask {
  title: string
  sortOrder: number
}

export async function decomposeTaskWithAI(
  provider: AIProvider,
  apiKey: string,
  taskTitle: string,
  taskMemo?: string
): Promise<DecomposedSubtask[]> {
  const systemPrompt = `You are a task decomposition assistant for a Korean to-do list app.
Break down the given task into 4-7 actionable subtasks.

Return ONLY valid JSON array (no markdown, no explanation):
[
  { "title": "subtask description", "sortOrder": 0 },
  ...
]

Rules:
- Each subtask should be specific and actionable
- Order subtasks logically (preparation → execution → review)
- Use Korean language for subtask titles
- Keep subtask titles concise (under 50 characters)
- Do NOT include the main task itself as a subtask`

  const userMessage = taskMemo
    ? `작업: ${taskTitle}\n메모: ${taskMemo}`
    : `작업: ${taskTitle}`

  const result = await callAI(
    provider,
    apiKey,
    systemPrompt,
    [{ role: 'user', content: userMessage }],
    'decomposeTask',
    512
  )

  try {
    const jsonMatch = result.match(/\[[\s\S]*\]/)
    if (!jsonMatch) throw new Error('JSON array not found')
    const parsed = JSON.parse(jsonMatch[0])
    return parsed.map((item: { title: string }, i: number) => ({
      title: item.title,
      sortOrder: i,
    }))
  } catch {
    throw new Error('AI 응답을 파싱할 수 없습니다. 다시 시도해주세요.')
  }
}

// ─── Productivity Summary ──────────────────────────

export interface ProductivitySummary {
  summary: string
  highlights: string[]
  suggestion: string
}

export async function generateProductivitySummary(
  provider: AIProvider,
  apiKey: string,
  stats: {
    totalCompleted: number
    totalPending: number
    completionRate: number
    topCategories: { name: string; count: number }[]
    recentDays: { date: string; completed: number }[]
    streak: number
  }
): Promise<ProductivitySummary> {
  const systemPrompt = `You are a productivity coach for a Korean to-do list app.
Analyze the user's task completion data and provide an encouraging summary.

Return ONLY valid JSON (no markdown, no explanation):
{
  "summary": "2-3 sentence productivity summary in Korean",
  "highlights": ["highlight 1", "highlight 2", "highlight 3"],
  "suggestion": "one actionable suggestion in Korean"
}

Rules:
- Be encouraging but realistic
- Use Korean language
- Reference specific numbers from the data
- Keep highlights to 2-4 items
- Make the suggestion specific and actionable`

  const userMessage = `지난 7일 생산성 데이터:
- 완료: ${stats.totalCompleted}건
- 대기중: ${stats.totalPending}건
- 완료율: ${Math.round(stats.completionRate)}%
- 연속 달성: ${stats.streak}일
- 카테고리별: ${stats.topCategories.map((c) => `${c.name}(${c.count}건)`).join(', ')}
- 일별 완료: ${stats.recentDays.map((d) => `${d.date}: ${d.completed}건`).join(', ')}`

  const result = await callAI(
    provider,
    apiKey,
    systemPrompt,
    [{ role: 'user', content: userMessage }],
    'productivity',
    512
  )

  try {
    const jsonMatch = result.match(/\{[\s\S]*\}/)
    if (!jsonMatch) throw new Error('JSON not found')
    return JSON.parse(jsonMatch[0])
  } catch {
    return {
      summary: `이번 주 ${stats.totalCompleted}개의 작업을 완료했습니다.`,
      highlights: [`완료율: ${Math.round(stats.completionRate)}%`],
      suggestion: '매일 꾸준히 작업을 완료해보세요.',
    }
  }
}

// ─── AI Task Grouping Suggestion ──────────────────

export async function suggestTaskGroups(
  provider: AIProvider,
  apiKey: string,
  tasks: { id: number; title: string; categoryName?: string; dueDate?: string; memo?: string }[]
): Promise<SuggestedGroup[]> {
  const systemPrompt = `You are a project management assistant for a Korean to-do list app.
Analyze the user's tasks and suggest logical groupings (3-5 groups).

Return ONLY valid JSON array (no markdown, no explanation):
[
  {
    "name": "group name in Korean",
    "description": "brief description in Korean",
    "color": "#hex color",
    "taskIds": [1, 2, 3],
    "reason": "why these tasks are grouped together in Korean"
  }
]

Rules:
- Group related tasks by theme, project, or goal
- Use Korean for all text
- Choose distinct hex colors for each group: #3B82F6, #10B981, #F59E0B, #EF4444, #8B5CF6, #EC4899, #06B6D4
- Each task should appear in at most one group
- Not all tasks need to be grouped — skip unrelated ones
- Provide 3-5 groups maximum
- Keep names concise (under 20 characters)`

  const taskList = tasks.map((t) =>
    `[${t.id}] ${t.title}${t.categoryName ? ` (${t.categoryName})` : ''}${t.dueDate ? ` 마감:${t.dueDate}` : ''}${t.memo ? ` 메모:${t.memo.slice(0, 50)}` : ''}`
  ).join('\n')

  const result = await callAI(
    provider,
    apiKey,
    systemPrompt,
    [{ role: 'user', content: `다음 할일들을 분석하여 관련 작업끼리 그룹으로 묶어주세요:\n\n${taskList}` }],
    'grouping',
    1024
  )

  try {
    const jsonMatch = result.match(/\[[\s\S]*\]/)
    if (!jsonMatch) throw new Error('JSON array not found')
    const parsed = JSON.parse(jsonMatch[0])
    return parsed.map((g: SuggestedGroup) => ({
      name: g.name,
      description: g.description || '',
      color: g.color || '#3B82F6',
      taskIds: Array.isArray(g.taskIds) ? g.taskIds : [],
      reason: g.reason || '',
    }))
  } catch {
    throw new Error('AI 응답을 파싱할 수 없습니다. 다시 시도해주세요.')
  }
}

// ─── AI Next Steps Suggestion ─────────────────────

export interface NextStepsSuggestion {
  summary: string
  nextSteps: string[]
  tip: string
}

export async function suggestNextSteps(
  provider: AIProvider,
  apiKey: string,
  groupName: string,
  tasks: { title: string; status: string; dueDate?: string }[]
): Promise<NextStepsSuggestion> {
  const systemPrompt = `You are a project management coach for a Korean to-do list app.
Analyze the group's task progress and suggest next steps.

Return ONLY valid JSON (no markdown, no explanation):
{
  "summary": "1-2 sentence progress summary in Korean",
  "nextSteps": ["step 1", "step 2", "step 3"],
  "tip": "one helpful tip in Korean"
}

Rules:
- Analyze which tasks are completed vs pending
- Suggest prioritized next actions
- Use Korean language
- Keep nextSteps to 2-4 items
- Make the tip specific to this group`

  const taskList = tasks.map((t) =>
    `[${t.status === 'completed' ? '✅' : '⬜'}] ${t.title}${t.dueDate ? ` (마감: ${t.dueDate})` : ''}`
  ).join('\n')

  const result = await callAI(
    provider,
    apiKey,
    systemPrompt,
    [{ role: 'user', content: `그룹: ${groupName}\n\n작업 목록:\n${taskList}` }],
    'nextSteps',
    512
  )

  try {
    const jsonMatch = result.match(/\{[\s\S]*\}/)
    if (!jsonMatch) throw new Error('JSON not found')
    return JSON.parse(jsonMatch[0])
  } catch {
    const completed = tasks.filter((t) => t.status === 'completed').length
    return {
      summary: `${tasks.length}개 작업 중 ${completed}개 완료됨.`,
      nextSteps: ['남은 작업을 확인하고 우선순위를 정해보세요.'],
      tip: '가장 긴급한 작업부터 처리하세요.',
    }
  }
}
